/** Static copy from Audit Practice Manual (2024 Edition).xlsm — Li / Ls / L2 / Lapp01–03; plus L1 / L4 WP pages */

import type {G0Page} from '@/pages/components/audit-program/g-section/constants';
import type {ESummaryObjective, ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import type {JBreakdownPage, JRemark} from '@/pages/components/audit-program/j-section/constants';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';

export type LSectionPageCode =
    | 'Li' | 'Ls' | 'L2'
    | 'L1' | 'L4'
    | 'Lapp01' | 'Lapp02' | 'Lapp03';

export const L_SECTION_PAGES: LSectionPageCode[] = [
    'Li', 'Ls', 'L2', 'L1', 'L4', 'Lapp01', 'Lapp02', 'Lapp03',
];

export const L_PAGE_TITLES: Record<LSectionPageCode, string> = {
    Li: 'Index',
    Ls: 'Summary sheet - Trade & other payables',
    L2: 'Audit programme - Trade & other payables',
    L1: 'Accrued expenses',
    L4: 'Search for unrecorded liabilities',
    Lapp01: 'Summary of trade payables circularization results',
    Lapp02: 'Trade payables circularization sheet',
    Lapp03: 'Supplementary procedures - Trade & other payables',
};

export type LSectionIndexRow = SectionIndexRow & {
    pageCode?: Exclude<LSectionPageCode, 'Li'>;
};

export const L_SECTION_INDEX_LETTER = 'L';
export const L_SECTION_INDEX_TITLE = 'TRADE & OTHER PAYABLES';

export const L_SECTION_INDEX: LSectionIndexRow[] = [
    {id: 'l-1', kind: 'item', num: '1', title: 'Lead schedule'},
    {id: 'l-2', kind: 'item', num: '2', title: 'Audit programme', pageCode: 'L2'},
    {id: 'l-3', kind: 'item', num: '3', title: 'List of payable balances'},
    {id: 'l-4', kind: 'item', num: '4', title: 'Accruals and provisions', pageCode: 'L1'},
    {id: 'l-5', kind: 'item', num: '5', title: "Directors' accounts: summary"},
    {id: 'l-6', kind: 'item', num: '6', title: 'Hire purchase/ leases'},
    {id: 'l-7', kind: 'item', num: '7', title: 'Summary of creditors circularization results', pageCode: 'Lapp01'},
    {id: 'l-8', kind: 'item', num: '8', title: 'Certificates/confirmation replies', pageCode: 'Lapp02'},
    {id: 'l-s', kind: 'item', num: 's', title: 'Summary sheet', pageCode: 'Ls'},
    {id: 'l-4wp', kind: 'item', num: 'search', title: 'Search for unrecorded liabilities', pageCode: 'L4'},
    {id: 'l-app03', kind: 'item', num: 'app03', title: 'Supplementary procedures', pageCode: 'Lapp03'},
    {id: 'l-9', kind: 'item', num: '9', title: ''},
    {id: 'l-10', kind: 'item', num: '10', title: ''},
    {id: 'l-11', kind: 'item', num: '11', title: ''},
];

export type LProgrammePage = G0Page;
export type LSummaryObjective = ESummaryObjective;
export type LSummaryPage = ESummaryPage;

export const LS_PAGE: LSummaryPage = {
    title: 'SUMMARY SHEET – TRADE & OTHER PAYABLES',
    objectivesTitle: 'Audit objectives',
    objectives: [
        {
            id: 'obj1',
            num: '1',
            text: 'To ensure that liabilities exist and have been fully and accurately recorded.',
            defaultKeyTests: '4, 5, 6, 7, 8, 9',
            defaultSuppRef: '1, 2, 3',
        },
        {
            id: 'obj2',
            num: '2',
            text: 'To ensure that proper cut-off has been applied.',
            defaultKeyTests: '10',
            defaultSuppRef: '',
        },
        {
            id: 'obj3',
            num: '3',
            text: 'To ensure that all leases have been accounted for in accordance with applicable accounting standards.',
            defaultKeyTests: '11, 12, 13, 14',
            defaultSuppRef: '4',
        },
        {
            id: 'obj4',
            num: '4',
            text: 'To ensure that all material accruals, loans and other payables have been properly accounted for.',
            defaultKeyTests: '15, 16, 17, 18',
            defaultSuppRef: '5, 6, 7',
        },
        {
            id: 'obj5',
            num: '5',
            text: 'To confirm that all necessary disclosures concerning payables have been made and that the information is appropriately presented and described.',
            defaultKeyTests: '19',
            defaultSuppRef: '',
        },
    ],
    planningTitle: 'Planning conclusion',
    finalTitle: 'Final conclusion',
    materialityLabel: 'Materiality (HK$)',
    planningNote:
        'I am satisfied that from the tests planned sufficient appropriate evidence can be gained to satisfy the objectives.',
    finalBullets: [
        'the work has been performed in accordance with the audit programme;',
        'the work performed and the results obtained have been adequately documented;',
        'all necessary information has been collected for the presentation and disclosure in the financial statements;',
        'sufficient appropriate evidence has been obtained to support the audit conclusion reached;',
        'in my opinion (subject to matters highlighted on B6 or B9) trade and other payables are fairly stated / not fairly stated as described below:',
    ],
};

export const L2_PAGE: LProgrammePage = {
    title: 'AUDIT PROGRAMME – TRADE & OTHER PAYABLES',
    rows: [
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Obtain and check, or prepare, a lead schedule for the current year\'s figures and reconcile this to the general ledger.', level: 0},
        {id: 'r3', kind: 'main', num: '2', text: 'Carry out analytical procedures such as:', level: 0},
        {id: 'r4', kind: 'sub', num: '(a)', text: 'comparison of the current figures with those of prior periods;', level: 1},
        {id: 'r5', kind: 'sub', num: '(b)', text: 'review and comparison of key ratios or other performance indicators;', level: 1},
        {id: 'r6', kind: 'sub', num: '(c)', text: 'comparison of the top 10 suppliers this year with last year.', level: 1},
        {id: 'r7', kind: 'main', num: '3', text: 'Review for items above performance materiality or that are otherwise unusual and verify.', level: 0},
        {id: 'r8', kind: 'section', num: '', text: 'Existence and completeness', level: 0},
        {id: 'r9', kind: 'main', num: '4', text: 'Obtain or prepare a list of trade payables.  Agree the list to the control account.  Obtain explanations for all material adjustments to the control account.', level: 0},
        {id: 'r10', kind: 'main', num: '5', text: 'Test the casts of the list.', level: 0},
        {id: 'r11', kind: 'main', num: '6', text: 'List all debit balances over HK$_______ and obtain explanations.  Ensure they are correctly treated in the accounts.', level: 0},
        {id: 'r12', kind: 'main', num: '7', text: 'Perform a trade payables\' circularization. Ensuring that control of the process is maintained:', level: 0},
        {id: 'r13', kind: 'sub', num: '(a)', text: 'Select a sample of accounts from a complete list of balances for example an activity report (include nil and debit balances where applicable).  Record the details on a working paper.', level: 1},
        {id: 'r14', kind: 'sub', num: '(b)', text: 'Confirm with client the trade payables you wish to circularize.', level: 1},
        {id: 'r15', kind: 'sub', num: '(c)', text: 'Obtain explanations where the client does not want you to circularize particular balances and consider alternative procedures that can be applied.', level: 1},
        {id: 'r16', kind: 'sub', num: '(d)', text: 'Consider whether the refusal to allow circularization of particular balances will impose a limitation of scope on the audit or is an indication of fraud.', level: 1},
        {id: 'r17', kind: 'sub', num: '(e)', text: 'Send the confirmation requests.  Enclose a prepaid envelope for return to the firm.  Ensure the reply part of the letter is properly referenced.', level: 1},
        {id: 'r18', kind: 'sub', num: '(f)', text: 'In respect of each reply:', level: 1},
        {id: 'r19', kind: 'sub', num: '(i)', text: 'record it on the control sheet;', level: 2},
        {id: 'r20', kind: 'sub', num: '(ii)', text: 'agree or reconcile the balance to that shown in the client\'s records;', level: 2},
        {id: 'r21', kind: 'sub', num: '(iii)', text: 'investigate any exceptions;', level: 2},
        {id: 'r22', kind: 'sub', num: '(iv)', text: 'consider whether any of the exceptions are indicative of fraud or other misstatement.', level: 2},
        {id: 'r23', kind: 'sub', num: '(g)', text: 'Where replies are not received within a reasonable period send a follow-up letter.', level: 1},
        {id: 'r24', kind: 'sub', num: '(h)', text: 'Perform the alternative procedures for any unfavourable responses or where no response is obtained.', level: 1},
        {id: 'r25', kind: 'sub', num: '(i)', text: 'Summarise the results and consider whether adequate audit comfort has been obtained for trade payables.', level: 1},
        {id: 'r26', kind: 'main', num: '8', text: 'Where a circularization is not carried out, or the results are unsatisfactory, test trade payables for completeness as follows:', level: 0},
        {id: 'r27', kind: 'sub', num: '(a)', text: 'obtain the year end trade payables listing;', level: 1},
        {id: 'r28', kind: 'sub', num: '(b)', text: 'compare to prior year to identify obvious omissions;', level: 1},
        {id: 'r29', kind: 'sub', num: '(c)', text: 'review purchase day book during the year and payments after the year-end to identify main suppliers;', level: 1},
        {id: 'r30', kind: 'sub', num: '(d)', text: 'review purchase YTD information where available to identify major suppliers;', level: 1},
        {id: 'r31', kind: 'sub', num: '(e)', text: 'enquire to major suppliers not on the list.  Ensure the list is in fact complete;', level: 1},
        {id: 'r32', kind: 'sub', num: '(f)', text: 'enquire into low balances with major suppliers to ensure they are complete;', level: 1},
        {id: 'r33', kind: 'sub', num: '(g)', text: 'obtain the file for year end supplier statements.  Check a sample and agree/reconcile to the list of balances;', level: 1},
        {id: 'r34', kind: 'sub', num: '(h)', text: 'for major suppliers (regardless of recorded balance outstanding) and for all material recorded balances, identify the related statement and agree/reconcile to the creditors\' listing;', level: 1},
        {id: 'r35', kind: 'sub', num: '(i)', text: 'where a statement is not available for any major supplier(s) consider requesting a copy statement from the supplier(s);', level: 1},
        {id: 'r36', kind: 'sub', num: '(j)', text: 'review after-date payments.  Ensure those paying off pre-year end items are included in creditors;', level: 1},
        {id: 'r37', kind: 'sub', num: '(k)', text: 'extend the scope of (i) above if statements are not retained for all suppliers.', level: 1},
        {id: 'r38', kind: 'main', num: '9', text: 'Review invoices in the new period to ensure all material amounts have been provided for.', level: 0},
        {id: 'r39', kind: 'section', num: '', text: 'Cut-off', level: 0},
        {id: 'r40', kind: 'main', num: '10', text: 'Cut-off test is covered in Section R.', level: 0},
        {id: 'r41', kind: 'section', num: '', text: 'Leasing', level: 0},
        {id: 'r42', kind: 'main', num: '11', text: 'Complete relevant programmes under App6 where HKFRS 16 applies.', level: 0},
        {id: 'r43', kind: 'main', num: '12', text: 'Where an accounting framework does not apply the approach in HKFRS 16: review the treatment of all leases.', level: 0},
        {id: 'r44', kind: 'sub', num: '(a)', text: 'Confirm that leases are correctly classified under the relevant accounting framework, for example, the distinction between operating and finance leases where applicable.', level: 1},
        {id: 'r45', kind: 'sub', num: '(b)', text: 'Consider any new agreements and ensure that the accounting treatment is in accordance with applicable accounting standards.', level: 1},
        {id: 'r46', kind: 'sub', num: '(c)', text: 'Confirm that any assets, liabilities or finance charges are accounted for in accordance with applicable accounting standards.', level: 1},
        {id: 'r47', kind: 'main', num: '13', text: 'Confirm that any lease incentives have been accounted for in accordance with applicable accounting standards.', level: 0},
        {id: 'r48', kind: 'main', num: '14', text: 'Have any sale and lease back transactions in the period been identified and accounted for in accordance with the applicable accounting standard?', level: 0},
        {id: 'r49', kind: 'section', num: '', text: 'Accruals', level: 0},
        {id: 'r50', kind: 'main', num: '15', text: 'Review accruals for completeness comparing to:', level: 0},
        {id: 'r51', kind: 'sub', num: '(a)', text: 'last year\'s list;', level: 1},
        {id: 'r52', kind: 'sub', num: '(b)', text: 'expectations.', level: 1},
        {id: 'r53', kind: 'section', num: '', text: 'Income Taxes', level: 0},
        {id: 'r54', kind: 'main', num: '16', text: 'Agree the current tax liability to Section P.', level: 0},
        {id: 'r55', kind: 'section', num: '', text: 'Provisions', level: 0},
        {id: 'r56', kind: 'main', num: '17', text: 'Carry out audit tests as detailed in Section N.', level: 0},
        {id: 'r57', kind: 'section', num: '', text: 'Other payables', level: 0},
        {id: 'r58', kind: 'main', num: '18', text: 'Vouch any other payables that are material to supporting documentation and ensure that other payables are carried at amortized cost.', level: 0},
        {id: 'r59', kind: 'section', num: '', text: 'Presentation and disclosure', level: 0},
        {id: 'r60', kind: 'main', num: '19', text: 'Ensure that there is sufficient appropriate evidence on the file to support all disclosures made.', level: 0},
        {id: 'r61', kind: 'section', num: '', text: 'Bespoke tests', level: 0},
        {id: 'r62', kind: 'main', num: '20', text: 'Draft and perform any necessary additional tests such as those required by C8.3 and cross reference with the objectives on the summary sheet.', level: 0},
        {id: 'r63', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r64', kind: 'main', num: '21', text: 'Consider whether there are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 0},
        {id: 'r65', kind: 'main', num: '22', text: 'Review the planned extent of reliance on internal controls in this area and consider whether this remains appropriate.', level: 0},
        {id: 'r66', kind: 'main', num: '23', text: 'Assess whether the initial materiality and/or risk assessment should be revised in view of the audit evidence obtained.  Record details of any necessary adjustments on B6 or B9.  Consider the impact on the remainder of the audit work and on any work undertaken to date.', level: 0},
    ],
};

/** @deprecated Prefer L2_PAGE — kept so old template seeds/refs still resolve. */
export const L0_PAGE = L2_PAGE;

export const LAPP03_PAGE: LProgrammePage = {
    title: 'SUPPLEMENTARY PROCEDURES - TRADE & OTHER PAYABLES',
    rows: [
        {id: 'r1', kind: 'main', num: '1', text: 'Ensure the entity has accounted for its trade and other payables in accordance with applicable accounting standards.', level: 0},
        {id: 'r2', kind: 'main', num: '2', text: 'Where trade payables are valued on a basis other than amortized cost using the effective interest method, ensure that the treatment is in accordance with applicable accounting standards.', level: 0},
        {id: 'r3', kind: 'main', num: '3', text: 'Where trade payables are carried at amortized cost, consider the reasonableness of the present value of estimated future cash flow in determining the amortized cost.', level: 0},
        {id: 'r4', kind: 'main', num: '4', text: 'For lease liability, consider:', level: 0},
        {id: 'r5', kind: 'sub', num: '(a)', text: 'Obtain direct confirmation of relevant details of outstanding lease payments.', level: 1},
        {id: 'r6', kind: 'sub', num: '(b)', text: 'Physically inspect the asset, if possible, or determine that the asset exists and is in use by the client.', level: 1},
        {id: 'r7', kind: 'main', num: '5', text: 'For bills payable, perform the following procedures:', level: 0},
        {id: 'r8', kind: 'sub', num: '(a)', text: 'Obtain a list of bills payable balances.', level: 1},
        {id: 'r9', kind: 'sub', num: '(b)', text: 'Obtain banking facilities letter to see if such facilities have been granted by bank.', level: 1},
        {id: 'r10', kind: 'sub', num: '(c)', text: 'Check details of bills payable balances to bills statements.', level: 1},
        {id: 'r11', kind: 'sub', num: '(d)', text: 'Check the bills payable balances to bank confirmation.', level: 1},
        {id: 'r12', kind: 'main', num: '6', text: 'Ensure the entity accounted for foreign currency transactions in accordance with applicable accounting standards.', level: 0},
        {id: 'r13', kind: 'main', num: '7', text: 'Confirm that all monetary liabilities have been translated at appropriate closing rate.', level: 0},
    ],
};

export type Lapp01Metric = {id: string; label: string; letter?: string};

export type Lapp01Page = {
    title: string;
    asAtLabel: string;
    metrics: Lapp01Metric[];
    conclusionLabel: string;
};

export const LAPP01_PAGE: Lapp01Page = {
    title: "SUMMARY OF TRADE PAYABLES' CIRCULARIZATION RESULTS",
    asAtLabel: 'As at',
    metrics: [
        {id: 'totalBalances', label: 'Total trade payable balances'},
        {id: 'circularized', label: 'Total balances circularized (A)', letter: 'A'},
        {id: 'confirmed', label: 'Balances confirmed by circularization (B)', letter: 'B'},
        {id: 'statement', label: 'Balances verified by supplier statement reconciliations (D)', letter: 'D'},
        {id: 'alternative', label: 'Balances verified by alternative procedures (F)', letter: 'F'},
        {id: 'totalVerified', label: 'Total (G)', letter: 'G'},
        {id: 'unverified', label: 'Unverified balances (A-G)'},
    ],
    conclusionLabel: 'Conclusion',
};

export type Lapp02Page = {
    title: string;
    blankRows: number;
    columns: Array<{id: string; label: string}>;
};

export const LAPP02_PAGE: Lapp02Page = {
    title: "Trade Payables' circularization sheet",
    blankRows: 10,
    columns: [
        {id: 'supplier', label: 'Supplier'},
        {id: 'balance', label: 'Balance HK$ (A)'},
        {id: 'confirmed', label: 'Balance confirmed HK$ (B)'},
        {id: 'unconfirmed', label: 'Unconfirmed (A-B) HK$ (C)'},
        {id: 'statement', label: 'Checked to statement HK$ (D)'},
        {id: 'remaining', label: 'Remaining unaudited (C-D) HK$ (E)'},
        {id: 'altProcedures', label: 'Alternative procedures (E)'},
        {id: 'altVerified', label: 'Verified by alt. procedures HK$ (F)'},
        {id: 'unverified', label: 'Unverified balances HK$'},
    ],
};

const COMMON_REMARKS: JRemark[] = [
    {tick: 'V', text: 'Checked to invoices'},
    {tick: 'b', text: "Per last year's working paper"},
    {tick: '>', text: 'Per ledger'},
    {tick: '^', text: 'Casting checked'},
    {tick: 'C/✓', text: 'confirmation sent and agreed'},
];

export type L1Page = JBreakdownPage;

export const L1_PAGE: L1Page = {
    title: 'Accrued expenses',
    subtitle: 'Accrued and other payable',
    amountLabel: 'Accrued expenses',
    breakdownTitle: 'Breakdown of accrued expenses is as follow:',
    cyLabel: 'Current year HK$',
    lyLabel: 'Last year HK$',
    conclusionLabel: 'Conclusion',
    conclusionPlaceholder: 'All accrued expenses exist and accurate.',
    remarksTitle: 'Remark',
    remarks: COMMON_REMARKS,
    blankBreakdownRows: 8,
};

export type L4Page = {
    title: string;
    objectiveLabel: string;
    objectivePlaceholder: string;
    methodLabel: string;
    methodPlaceholder: string;
    resultLabel: string;
    resultPlaceholder: string;
    conclusionLabel: string;
    conclusionPlaceholder: string;
    remarksTitle: string;
    remarks: JRemark[];
};

export const L4_PAGE: L4Page = {
    title: 'Search for unrecorded liabilities',
    objectiveLabel: 'Objective',
    objectivePlaceholder: 'To confirm the completeness of accruals and search for unrecorded liabilities.',
    methodLabel: 'Method',
    methodPlaceholder: 'Review payments after year end above performance materiality / threshold.',
    resultLabel: 'Result',
    resultPlaceholder: '',
    conclusionLabel: 'Conclusion',
    conclusionPlaceholder: 'All accrued expenses exist and accurate.',
    remarksTitle: 'Remark',
    remarks: [
        {tick: '<', text: 'Per bank statement'},
        {tick: 'V', text: 'Checked to invoices'},
        {tick: '✓', text: 'Confirmed and agreed'},
        {tick: 'b', text: "Per last year's working paper"},
        {tick: '>', text: 'Per ledger'},
        {tick: '^', text: 'Casting checked'},
    ],
};
